// Sprint 8 — preferências do user agora server-side (users.settings via
// /auth/me/settings). Mantemos cache em localStorage como mirror pra:
//   1. Renderizar imediatamente no boot (sem flicker enquanto fetch resolve)
//   2. Funcionar offline pra leitura
//
// Hidratação: AuthProvider chama hydrateAppSettingsFromServer() após login,
// que faz GET /auth/me/settings e atualiza o cache + DOM.
//
// Save: setAndSyncAppSettings persiste no server (PUT) E no cache.

import { authApi } from '../lib/api';

export interface AppSettings {
  accentColor: string;
  logo: string;
  darkMode: boolean;
  fontFamily: string;
}

const SETTINGS_KEY = 'fb:app-settings';

export const DEFAULT_APP_SETTINGS: AppSettings = {
  accentColor: '#6366f1',
  logo: '',
  darkMode: false,
  fontFamily: 'Inter, system-ui, sans-serif',
};

function normalize(raw: unknown): AppSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_APP_SETTINGS };
  const r = raw as Partial<AppSettings>;
  return {
    accentColor: typeof r.accentColor === 'string' ? r.accentColor : DEFAULT_APP_SETTINGS.accentColor,
    logo:        typeof r.logo === 'string'        ? r.logo        : DEFAULT_APP_SETTINGS.logo,
    darkMode:    typeof r.darkMode === 'boolean'   ? r.darkMode    : DEFAULT_APP_SETTINGS.darkMode,
    fontFamily:  typeof r.fontFamily === 'string'  ? r.fontFamily  : DEFAULT_APP_SETTINGS.fontFamily,
  };
}

/** Lê do localStorage (cache local). Sync, sem fetch. */
export function loadAppSettings(): AppSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? normalize(JSON.parse(raw)) : { ...DEFAULT_APP_SETTINGS };
  } catch {
    return { ...DEFAULT_APP_SETTINGS };
  }
}

/** Salva só no localStorage (sem PUT). Usado depois de hidratar do server. */
export function saveAppSettings(settings: AppSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

/**
 * Salva no localStorage + PUT no servidor. Falha de rede não trava UI;
 * cache local fica atualizado pra próxima sessão tentar sincronizar.
 */
export async function setAndSyncAppSettings(settings: AppSettings): Promise<void> {
  saveAppSettings(settings);
  try {
    await authApi.saveSettings(settings as unknown as Record<string, unknown>);
  } catch {
    // Erro silenciado — cache local serve até a próxima tentativa.
    // (UI pode mostrar toast via callers se quiser.)
  }
}

/**
 * Após login, hidrata o cache local com o que tá no servidor. Aplica no
 * DOM imediatamente. Idempotente: se servidor não tem nada, fallback pro
 * cache local atual (preserva localStorage de sessões anteriores).
 */
export async function hydrateAppSettingsFromServer(): Promise<AppSettings> {
  try {
    const r = await authApi.getSettings();
    const fromServer = r.settings;
    if (fromServer && Object.keys(fromServer).length > 0) {
      const merged = normalize(fromServer);
      saveAppSettings(merged);
      applyAppSettings(merged);
      return merged;
    }
  } catch {
    // 401 ou outro erro — fica com o cache local
  }
  const local = loadAppSettings();
  applyAppSettings(local);
  return local;
}

export function applyAppSettings(settings: AppSettings): void {
  const root = document.documentElement;
  root.style.setProperty('--app-accent', settings.accentColor);
  root.style.setProperty('--app-accent-10', settings.accentColor + '1a');
  root.style.setProperty('--app-accent-20', settings.accentColor + '33');
  if (settings.darkMode) {
    document.body.classList.add('app-dark');
  } else {
    document.body.classList.remove('app-dark');
  }
  if (settings.fontFamily) {
    document.body.style.fontFamily = settings.fontFamily;
  }
}
